import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { fmtDate } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, Pill, Loading } from "@/components/kit";

const TYPE = { shift: "Shift", banking: "Banking", store_delivery: "Stock delivery", lpg_delivery: "LPG delivery", stock_adjustment: "Stock count", store_transfer: "Stock transfer" };
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");

export default function Station() {
  const { company } = useCompany();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (company) setData((await api.get(`/companies/${company.id}/station-link`)).data);
  }, [company]);
  useEffect(() => { load().catch(() => setData({ stations: [], linked: [], syncs: [], docs: [], errors: 0 })); }, [load]);
  const sync = async () => {
    setBusy(true);
    try {
      const res = (await api.post(`/companies/${company.id}/station-link/sync`)).data;
      const failed = res.find((r) => r.failure);
      if (failed) toast.error(`${failed.station}: ${failed.failure}`);
      else {
        const n = res.reduce((a, r) => a + (r.posted || 0) + (r.reposted || 0) + (r.removed || 0), 0);
        toast.success(n ? `${n} updated from the station` : "Up to date");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (!data) return <Page title="Station link"><Loading /></Page>;
  return (
    <Page title="Station link" actions={<Btn kind="primary" onClick={sync} disabled={busy}>{busy ? "Syncing…" : "Sync now"}</Btn>}>
      <p className="mt-2 max-w-3xl text-sm text-[#5F5C58]">
        Submitted shifts, stock deliveries and stock counts are posted here by themselves every few minutes. A shift
        corrected at the station is reversed and posted again - correct it there, not here.
      </p>
      <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {data.stations.map((s) => {
          const st = data.syncs.find((x) => x.station === s);
          const linked = data.linked.includes(s);
          return (
            <section key={s} className="rounded-[10px] border border-[#E4E2DE] bg-white p-4 text-sm">
              <div className="flex items-center justify-between"><span className="font-semibold">{s}</span>
                {!linked ? <Pill kind="grey">Not linked</Pill> : st?.failed_at && (!st.at || st.failed_at > st.at) ? <Pill kind="red">Can't reach</Pill> : <Pill kind="green">Linked</Pill>}
              </div>
              <div className="mt-2 text-[#5F5C58]">Last sync: {when(st?.at)}</div>
              {st?.failed_at && (!st.at || st.failed_at > st.at) && <div className="mt-1 text-[#A11D1D]">{st.failure}</div>}
            </section>
          );
        })}
      </div>
      {data.errors > 0 && <p className="mt-4 text-sm font-semibold text-[#A11D1D]">{data.errors} not posted - see below</p>}
      <Table minWidth={720} cols={[{ label: "Date" }, { label: "What" }, { label: "Status" }, { label: "Journals", align: "right" }]}>
        {data.docs.map((d) => (
          <Tr key={d.id}>
            <Td>{fmtDate(d.date)}</Td>
            <Td>{TYPE[d.type] || d.type}{d.label ? ` · ${d.label}` : ""}</Td>
            <Td>{d.status === "posted" ? <Pill kind="green">Posted</Pill> : <><Pill kind="red">Not posted</Pill><div className="mt-1 text-xs text-[#A11D1D]">{d.error}</div></>}</Td>
            <Td right>{(d.journal_ids || []).length ? <Link to="/journals" className="text-sky-700 hover:underline">{d.journal_ids.length}</Link> : "–"}</Td>
          </Tr>
        ))}
      </Table>
      {data.docs.length === 0 && <p className="py-8 text-center text-sm text-[#5F5C58]">Nothing from the station yet</p>}
    </Page>
  );
}
